import { PlusOutlined } from '@ant-design/icons';
import type { FormInstance, FormItemProps } from 'antd';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import Empty from '@/components/QuietEmpty';
import type { ReferralItem } from '@/services/bff/types/referral';
import { getResponsiveModalWidth } from '../../utils/modal-width';
import businessModalStyles from '../business-modal/style.module.less';
import styles from './style.module.less';

const { Title } = Typography;

type ReferralEditorMode = 'create' | 'edit';
type ReferralBlockKey = 'incoming' | 'outgoing';

type ReferralEditorContext<TValues extends Record<string, any>, TRecord> = {
  form: FormInstance<TValues>;
  mode: ReferralEditorMode;
  submitting?: boolean;
  record?: TRecord;
};

type ReferralFormFieldBase<TValues extends Record<string, any>, TRecord> = {
  name: keyof TValues & string;
  label: ReactNode;
  rules?: FormItemProps['rules'];
  hidden?: (
    context: ReferralEditorContext<TValues, TRecord>,
  ) => boolean | undefined;
};

type ReferralFieldOption = {
  label: ReactNode;
  value: string | number | boolean;
  disabled?: boolean;
};

type ReferralRadioField<
  TValues extends Record<string, any>,
  TRecord,
> = ReferralFormFieldBase<TValues, TRecord> & {
  type: 'radio';
  options:
    | ReferralFieldOption[]
    | ((
        context: ReferralEditorContext<TValues, TRecord>,
      ) => ReferralFieldOption[]);
  resetNames?: Array<keyof TValues & string>;
  onValueChange?: (
    value: string | number | boolean | undefined,
    context: ReferralEditorContext<TValues, TRecord>,
  ) => void;
};

type ReferralSelectField<
  TValues extends Record<string, any>,
  TRecord,
> = ReferralFormFieldBase<TValues, TRecord> & {
  type: 'select';
  options?:
    | ReferralFieldOption[]
    | ((
        context: ReferralEditorContext<TValues, TRecord>,
      ) => ReferralFieldOption[]);
  placeholder?:
    | string
    | ((context: ReferralEditorContext<TValues, TRecord>) => string);
  disabled?:
    | boolean
    | ((context: ReferralEditorContext<TValues, TRecord>) => boolean);
  allowClear?: boolean;
  showSearch?: boolean;
  filterOption?: boolean;
  resetNames?: Array<keyof TValues & string>;
  onValueChange?: (
    value: string | number | boolean | undefined,
    context: ReferralEditorContext<TValues, TRecord>,
  ) => void;
  onLoadOptions?: (
    keyword: string | undefined,
    context: ReferralEditorContext<TValues, TRecord>,
  ) => void | Promise<void>;
  emptyAction?: (context: ReferralEditorContext<TValues, TRecord>) => ReactNode;
};

type ReferralDateField<
  TValues extends Record<string, any>,
  TRecord,
> = ReferralFormFieldBase<TValues, TRecord> & {
  type: 'date';
  showTime?: boolean;
  placeholder?: string;
};

type ReferralSwitchField<
  TValues extends Record<string, any>,
  TRecord,
> = ReferralFormFieldBase<TValues, TRecord> & {
  type: 'switch';
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
};

type ReferralNumberField<
  TValues extends Record<string, any>,
  TRecord,
> = ReferralFormFieldBase<TValues, TRecord> & {
  type: 'number';
  min?: number;
  max?: number;
  precision?: number;
  addonAfter?: ReactNode;
  placeholder?: string;
};

type ReferralTextareaField<
  TValues extends Record<string, any>,
  TRecord,
> = ReferralFormFieldBase<TValues, TRecord> & {
  type: 'textarea';
  rows?: number;
  maxLength?: number;
  showCount?: boolean;
  placeholder?: string;
};

type ReferralFormField<TValues extends Record<string, any>, TRecord> =
  | ReferralRadioField<TValues, TRecord>
  | ReferralSelectField<TValues, TRecord>
  | ReferralDateField<TValues, TRecord>
  | ReferralSwitchField<TValues, TRecord>
  | ReferralNumberField<TValues, TRecord>
  | ReferralTextareaField<TValues, TRecord>;

type ReferralBlockEditorConfig<
  TValues extends Record<string, any>,
  TRecord extends ReferralItem,
> = {
  form: FormInstance<TValues>;
  createTitle: string;
  editTitle: string;
  fields: Array<ReferralFormField<TValues, TRecord>>;
  submitting?: boolean;
  /** Show the create action without exposing row editing. */
  createOnly?: boolean;
  /** Use the shared viewport bounds and a responsive field grid. */
  responsive?: boolean;
  buildInitialValues?: (record?: TRecord) => Partial<TValues> | undefined;
  onOpen?: (
    record: TRecord | undefined,
    mode: ReferralEditorMode,
  ) => void | Promise<void>;
  onClose?: () => void;
  onSubmit: (args: {
    mode: ReferralEditorMode;
    record?: TRecord;
    values: TValues;
  }) => Promise<void> | void;
};

type ReferralBlockConfig<
  TRecord extends ReferralItem,
  TValues extends Record<string, any>,
> = {
  key: ReferralBlockKey;
  title: string;
  createButtonText?: string;
  items: TRecord[];
  columns: ColumnsType<TRecord>;
  emptyText: string;
  rowKey?: string | ((record: TRecord) => React.Key);
  tableScroll?: { x?: number | true | string };
  editor?: ReferralBlockEditorConfig<TValues, TRecord>;
  onDelete?: (record: TRecord) => void;
};

type ReferralSectionProps = {
  title?: string;
  hint?: string;
  renderSection: (args: {
    title: string;
    hint?: string;
    children: ReactNode;
  }) => ReactNode;
  blocks: Array<ReferralBlockConfig<any, any>>;
};

type ActiveEditorState = {
  blockKey: ReferralBlockKey;
  mode: ReferralEditorMode;
  record?: ReferralItem;
};

const getFieldContext = <
  TValues extends Record<string, any>,
  TRecord extends ReferralItem,
>(
  form: FormInstance<TValues>,
  mode: ReferralEditorMode,
  record?: TRecord,
  submitting?: boolean,
): ReferralEditorContext<TValues, TRecord> => ({
  form,
  mode,
  record,
  submitting,
});

const resolveMaybeFn = <T,>(
  value: T | ((...args: any[]) => T),
  ...args: any[]
): T =>
  typeof value === 'function'
    ? (value as (...rest: any[]) => T)(...args)
    : value;

function renderField<
  TValues extends Record<string, any>,
  TRecord extends ReferralItem,
>(
  field: ReferralFormField<TValues, TRecord>,
  context: ReferralEditorContext<TValues, TRecord>,
) {
  if (field.hidden?.(context)) {
    return null;
  }

  const resetFields = () => {
    if (!('resetNames' in field) || !field.resetNames?.length) {
      return;
    }
    field.resetNames.forEach((name) => {
      context.form.setFieldValue(name as any, undefined);
    });
  };

  switch (field.type) {
    case 'radio':
      return (
        <Form.Item
          key={field.name}
          name={field.name as any}
          label={field.label}
          rules={field.rules}
        >
          <Radio.Group
            options={resolveMaybeFn(field.options, context)}
            onChange={(event) => {
              resetFields();
              field.onValueChange?.(event?.target?.value, context);
            }}
          />
        </Form.Item>
      );
    case 'select': {
      const options = field.options
        ? resolveMaybeFn(field.options, context)
        : [];
      const disabled = field.disabled
        ? resolveMaybeFn(field.disabled, context)
        : false;
      const placeholder = field.placeholder
        ? resolveMaybeFn(field.placeholder, context)
        : undefined;
      const emptyAction = field.emptyAction?.(context);
      return (
        <Form.Item
          key={field.name}
          name={field.name as any}
          label={field.label}
          rules={field.rules}
        >
          <Select
            showSearch={field.showSearch ?? Boolean(field.onLoadOptions)}
            allowClear={field.allowClear ?? true}
            filterOption={field.filterOption ?? false}
            optionFilterProp="label"
            disabled={disabled || context.submitting}
            options={options}
            placeholder={placeholder}
            notFoundContent={
              emptyAction ? (
                <Space orientation="vertical" style={{ width: '100%' }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={false}
                  />
                  {emptyAction}
                </Space>
              ) : undefined
            }
            onFocus={() => {
              void field.onLoadOptions?.(undefined, context);
            }}
            onSearch={(keyword) => {
              void field.onLoadOptions?.(keyword, context);
            }}
            onChange={(value) => {
              resetFields();
              field.onValueChange?.(value, context);
            }}
          />
        </Form.Item>
      );
    }
    case 'date':
      return (
        <Form.Item
          key={field.name}
          name={field.name as any}
          label={field.label}
          rules={field.rules}
        >
          <DatePicker
            showTime={field.showTime}
            style={{ width: '100%' }}
            placeholder={field.placeholder}
          />
        </Form.Item>
      );
    case 'switch':
      return (
        <Form.Item
          key={field.name}
          name={field.name as any}
          label={field.label}
          rules={field.rules}
          valuePropName="checked"
        >
          <Switch
            checkedChildren={field.checkedChildren}
            unCheckedChildren={field.unCheckedChildren}
          />
        </Form.Item>
      );
    case 'number':
      return (
        <Form.Item
          key={field.name}
          name={field.name as any}
          label={field.label}
          rules={field.rules}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={field.min}
            max={field.max}
            precision={field.precision}
            addonAfter={field.addonAfter}
            placeholder={field.placeholder}
          />
        </Form.Item>
      );
    case 'textarea':
      return (
        <Form.Item
          key={field.name}
          name={field.name as any}
          label={field.label}
          rules={field.rules}
        >
          <Input.TextArea
            rows={field.rows}
            maxLength={field.maxLength}
            showCount={field.showCount}
            placeholder={field.placeholder}
          />
        </Form.Item>
      );
    default:
      return null;
  }
}

export function ReferralSection(props: ReferralSectionProps) {
  const { hint, renderSection, blocks, title = '介绍关系' } = props;
  const [activeEditor, setActiveEditor] = useState<ActiveEditorState | null>(
    null,
  );
  const currentBlock = useMemo(
    () => blocks.find((item) => item.key === activeEditor?.blockKey),
    [activeEditor?.blockKey, blocks],
  );
  const currentEditor = currentBlock?.editor;

  const submitLock = useRef(false);
  const [saving, setSaving] = useState(false);

  const closeEditor = useCallback(() => {
    if (submitLock.current || currentEditor?.submitting) return;
    if (currentEditor) {
      currentEditor.onClose?.();
      currentEditor.form.resetFields();
    }
    setActiveEditor(null);
  }, [currentEditor]);

  const openEditor = useCallback(
    async (
      block: ReferralBlockConfig<any, any>,
      mode: ReferralEditorMode,
      record?: ReferralItem,
    ) => {
      if (!block.editor) {
        return;
      }
      block.editor.form.resetFields();
      const initialValues = block.editor.buildInitialValues?.(record) || {};
      block.editor.form.setFieldsValue(initialValues);
      await block.editor.onOpen?.(record, mode);
      setActiveEditor({
        blockKey: block.key,
        mode,
        record,
      });
    },
    [],
  );

  const submitEditor = useCallback(async () => {
    if (!currentEditor || !activeEditor) {
      return;
    }
    if (submitLock.current || currentEditor.submitting) return;
    submitLock.current = true;
    setSaving(true);
    try {
      const values = await currentEditor.form.validateFields();
      await currentEditor.onSubmit({
        mode: activeEditor.mode,
        record: activeEditor.record,
        values,
      });
      submitLock.current = false;
      currentEditor.onClose?.();
      currentEditor.form.resetFields();
      setActiveEditor(null);
    } catch {
      // Validation stays inline; submit handlers display API failures.
    } finally {
      submitLock.current = false;
      setSaving(false);
    }
  }, [activeEditor, currentEditor]);

  return renderSection({
    title,
    hint,
    children: (
      <>
        {blocks.map((block, index) => {
          const normalizedColumns = block.columns.map((column, columnIndex) =>
            columnIndex === 0
              ? {
                  ...column,
                  fixed: column.fixed || 'left',
                }
              : column,
          );
          const actionColumns: ColumnsType<any> =
            (block.editor && !block.editor.createOnly) || block.onDelete
              ? [
                  {
                    title: '操作',
                    key: 'actions',
                    width: 120,
                    fixed: 'right',
                    render: (_: unknown, record: ReferralItem) => (
                      <Space size={8} wrap={false}>
                        {block.editor && !block.editor.createOnly ? (
                          <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() =>
                              void openEditor(block, 'edit', record)
                            }
                          >
                            编辑
                          </Button>
                        ) : null}
                        {block.onDelete ? (
                          <Button
                            type="link"
                            danger
                            style={{ padding: 0 }}
                            onClick={() => block.onDelete?.(record)}
                          >
                            删除
                          </Button>
                        ) : null}
                      </Space>
                    ),
                  },
                ]
              : [];

          return (
            <div
              key={block.key}
              style={{
                marginTop: index === 0 ? 0 : 16,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <Title level={5} style={{ margin: 0 }}>
                  {block.title}
                </Title>
                {block.editor ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => void openEditor(block, 'create')}
                  >
                    {block.createButtonText || '新增'}
                  </Button>
                ) : null}
              </div>
              <Table<any>
                rowKey={block.rowKey || 'uuid'}
                columns={[...normalizedColumns, ...actionColumns]}
                dataSource={block.items}
                pagination={false}
                size="small"
                scroll={block.tableScroll || { x: 'max-content' }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={false}
                    />
                  ),
                }}
              />
            </div>
          );
        })}
        {currentBlock && currentEditor && activeEditor ? (
          <Modal
            closable={{ 'aria-label': '关闭' }}
            keyboard
            centered={currentEditor.responsive || undefined}
            open
            title={
              activeEditor.mode === 'edit'
                ? currentEditor.editTitle
                : currentEditor.createTitle
            }
            onCancel={closeEditor}
            onOk={() => void submitEditor()}
            confirmLoading={saving || currentEditor.submitting}
            cancelButtonProps={{ disabled: saving || currentEditor.submitting }}
            width={
              currentEditor.responsive
                ? getResponsiveModalWidth('large')
                : undefined
            }
            className={
              currentEditor.responsive
                ? [
                    businessModalStyles.modal,
                    businessModalStyles.form,
                    styles.responsiveModal,
                  ].join(' ')
                : undefined
            }
            destroyOnHidden
          >
            <Form
              form={currentEditor.form}
              layout="vertical"
              preserve={false}
              disabled={saving || currentEditor.submitting}
            >
              {currentEditor.fields.map((field) => (
                <Form.Item key={field.name} noStyle shouldUpdate>
                  {() => {
                    const content = renderField(
                      field as ReferralFormField<
                        Record<string, any>,
                        ReferralItem
                      >,
                      getFieldContext(
                        currentEditor.form as FormInstance<Record<string, any>>,
                        activeEditor.mode,
                        activeEditor.record,
                        saving || currentEditor.submitting,
                      ),
                    );
                    return currentEditor.responsive && content ? (
                      <div
                        className={
                          field.type === 'textarea'
                            ? styles.fullWidth
                            : undefined
                        }
                      >
                        {content}
                      </div>
                    ) : (
                      content
                    );
                  }}
                </Form.Item>
              ))}
            </Form>
          </Modal>
        ) : null}
      </>
    ),
  });
}

export type {
  ReferralBlockConfig,
  ReferralBlockEditorConfig,
  ReferralEditorContext,
  ReferralFieldOption,
  ReferralFormField,
  ReferralSectionProps,
};
